import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Award, Users, Globe, Heart } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AboutPage = () => {
  const { t, i18n } = useTranslation();
  const [settings, setSettings] = useState(null);
  
  const seoTitles = {
    it: 'Chi Siamo | Offroad Italia - Tour Moto dal 2011',
    en: 'About Us | Offroad Italia - Motorcycle Tours Since 2011',
    de: 'Über Uns | Offroad Italia - Motorradtouren seit 2011',
    fr: 'À Propos | Offroad Italia - Tours Moto depuis 2011',
    es: 'Quiénes Somos | Offroad Italia - Tours Moto desde 2011'
  };
  
  const seoDescriptions = {
    it: 'Scopri la storia di Offroad Italia e del fondatore Ugo Filosa. 14+ anni di esperienza, 2000+ partecipanti, 200+ recensioni 5 stelle.',
    en: 'Discover the story of Offroad Italia and founder Ugo Filosa. 14+ years experience, 2000+ participants, 200+ 5-star reviews.',
    de: 'Entdecken Sie die Geschichte von Offroad Italia und Gründer Ugo Filosa. 14+ Jahre Erfahrung, 2000+ Teilnehmer, 200+ 5-Sterne-Bewertungen.',
    fr: "Découvrez l'histoire d'Offroad Italia et du fondateur Ugo Filosa. 14+ ans d'expérience, 2000+ participants, 200+ avis 5 étoiles.",
    es: 'Descubre la historia de Offroad Italia y el fundador Ugo Filosa. 14+ años de experiencia, 2000+ participantes, 200+ reseñas 5 estrellas.'
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const getAboutText = () => {
    if (!settings) return defaultAbout;
    const lang = i18n.language;
    const text = settings[`about_${lang}`];
    return text || settings.about_it || defaultAbout;
  };

  const defaultAbout = `Dal 2011, Offroad Italia organizza tour offroad di successo in Europa, Africa e Sudamerica che vedono iscritti centinaia di appassionati di offroad da tutta Europa e Nord Africa.

La nostra missione è offrire esperienze uniche e indimenticabili agli amanti delle due ruote, attraverso itinerari studiati nei minimi dettagli, assistenza professionale e la passione che ci contraddistingue.

I nostri "ENDURING" sono diventati un punto di riferimento nel panorama offroad internazionale, attirando piloti da ogni parte del mondo che cercano avventura, sfida e camaraterie.`;

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title={seoTitles[i18n.language] || seoTitles.it}
        description={seoDescriptions[i18n.language] || seoDescriptions.it}
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold text-white mb-4">{t('about.title')}</h1>
          <p className="text-xl text-blue-100">{t('about.subtitle')}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Story */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 text-lg whitespace-pre-line leading-relaxed">
              {getAboutText()}
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <Award className="mx-auto mb-4 text-blue-600" size={48} />
            <div className="text-4xl font-bold text-gray-900 mb-2">14+</div>
            <div className="text-gray-600">{t('about.experience')}</div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <Users className="mx-auto mb-4 text-blue-600" size={48} />
            <div className="text-4xl font-bold text-gray-900 mb-2">2000+</div>
            <div className="text-gray-600">{t('about.participants')}</div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <Globe className="mx-auto mb-4 text-blue-600" size={48} />
            <div className="text-4xl font-bold text-gray-900 mb-2">15+</div>
            <div className="text-gray-600">{t('about.countries')}</div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <Heart className="mx-auto mb-4 text-blue-600" size={48} />
            <div className="text-4xl font-bold text-gray-900 mb-2">200+</div>
            <div className="text-gray-600">{t('about.reviews')}</div>
          </div>
        </div>

        {/* Founder Section */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <img 
                src="/images/ugo-filosa.jpg" 
                alt="Ugo Filosa"
                className="rounded-lg shadow-lg w-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Ugo Filosa</h2>
              <p className="text-xl text-blue-600 mb-4">
                {i18n.language === 'it' ? 'Fondatore & CEO' : i18n.language === 'en' ? 'Founder & CEO' : i18n.language === 'de' ? 'Gründer & CEO' : i18n.language === 'fr' ? 'Fondateur & PDG' : 'Fundador & CEO'}
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                {i18n.language === 'it' ? "Con oltre 14 anni di esperienza nell'organizzazione di eventi offroad, Ugo Filosa ha trasformato la sua passione per le moto in un punto di riferimento per gli appassionati di tutta Europa." : i18n.language === 'en' ? 'With over 14 years of experience organizing offroad events, Ugo Filosa has transformed his passion for motorcycles into a reference point for enthusiasts across Europe.' : i18n.language === 'de' ? 'Mit über 14 Jahren Erfahrung in der Organisation von Offroad-Events hat Ugo Filosa seine Leidenschaft für Motorräder in einen Bezugspunkt für Enthusiasten in ganz Europa verwandelt.' : i18n.language === 'fr' ? "Avec plus de 14 ans d'expérience dans l'organisation d'événements offroad, Ugo Filosa a transformé sa passion pour les motos en un point de référence pour les passionnés de toute l'Europe." : 'Con más de 14 años de experiencia organizando eventos offroad, Ugo Filosa ha transformado su pasión por las motos en un punto de referencia para los entusiastas de toda Europa.'}
              </p>
              <p className="text-gray-700 leading-relaxed">
                {i18n.language === 'it' ? "Esperto pilota e organizzatore, Ugo guida personalmente molti dei nostri tour, garantendo sicurezza, professionalità e un'esperienza indimenticabile per ogni partecipante." : i18n.language === 'en' ? 'Expert rider and organizer, Ugo personally leads many of our tours, ensuring safety, professionalism, and an unforgettable experience for every participant.' : i18n.language === 'de' ? 'Als erfahrener Fahrer und Organisator leitet Ugo persönlich viele unserer Touren und gewährleistet Sicherheit, Professionalität und ein unvergessliches Erlebnis für jeden Teilnehmer.' : i18n.language === 'fr' ? 'Pilote expert et organisateur, Ugo dirige personnellement de nombreux de nos tours, garantissant sécurité, professionnalisme et une expérience inoubliable pour chaque participant.' : 'Piloto experto y organizador, Ugo dirige personalmente muchos de nuestros tours, garantizando seguridad, profesionalidad y una experiencia inolvidable para cada participante.'}
              </p>
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {i18n.language === 'it' ? 'Sicurezza' : i18n.language === 'en' ? 'Safety' : i18n.language === 'de' ? 'Sicherheit' : i18n.language === 'fr' ? 'Sécurité' : 'Seguridad'}
            </h3>
            <p className="text-gray-700">
              {i18n.language === 'it' ? 'La sicurezza dei nostri partecipanti è la nostra priorità assoluta. Assistenza meccanica, tracking satellitare e staff esperto sempre al tuo fianco.' : i18n.language === 'en' ? 'The safety of our participants is our absolute priority. Mechanical assistance, satellite tracking, and expert staff always at your side.' : i18n.language === 'de' ? 'Die Sicherheit unserer Teilnehmer ist unsere oberste Priorität. Mechanische Unterstützung, Satellitentracking und erfahrenes Personal immer an Ihrer Seite.' : i18n.language === 'fr' ? 'La sécurité de nos participants est notre priorité absolue. Assistance mécanique, suivi satellite et personnel expert toujours à vos côtés.' : 'La seguridad de nuestros participantes es nuestra prioridad absoluta. Asistencia mecánica, seguimiento por satélite y personal experto siempre a tu lado.'}
            </p>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {i18n.language === 'it' ? 'Passione' : i18n.language === 'en' ? 'Passion' : i18n.language === 'de' ? 'Leidenschaft' : i18n.language === 'fr' ? 'Passion' : 'Pasión'}
            </h3>
            <p className="text-gray-700">
              {i18n.language === 'it' ? 'Viviamo e respiriamo offroad. Ogni evento è organizzato con la massima dedizione per offrirti emozioni autentiche e indimenticabili.' : i18n.language === 'en' ? 'We live and breathe offroad. Every event is organized with maximum dedication to offer you authentic and unforgettable emotions.' : i18n.language === 'de' ? 'Wir leben und atmen Offroad. Jedes Event wird mit maximaler Hingabe organisiert, um Ihnen authentische und unvergessliche Emotionen zu bieten.' : i18n.language === 'fr' ? "Nous vivons et respirons l'offroad. Chaque événement est organisé avec un dévouement maximum pour vous offrir des émotions authentiques et inoubliables." : 'Vivimos y respiramos offroad. Cada evento está organizado con máxima dedicación para ofrecerte emociones auténticas e inolvidables.'}
            </p>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {i18n.language === 'it' ? 'Comunità' : i18n.language === 'en' ? 'Community' : i18n.language === 'de' ? 'Gemeinschaft' : i18n.language === 'fr' ? 'Communauté' : 'Comunidad'}
            </h3>
            <p className="text-gray-700">
              {i18n.language === 'it' ? 'Non organizziamo solo eventi, creiamo una famiglia di appassionati. Amicizie che durano una vita nascono sui nostri tour.' : i18n.language === 'en' ? "We don't just organize events, we create a family of enthusiasts. Friendships that last a lifetime are born on our tours." : i18n.language === 'de' ? 'Wir organisieren nicht nur Events, wir schaffen eine Familie von Enthusiasten. Freundschaften, die ein Leben lang halten, entstehen auf unseren Touren.' : i18n.language === 'fr' ? "Nous n'organisons pas seulement des événements, nous créons une famille de passionnés. Des amitiés qui durent toute une vie naissent lors de nos tours." : 'No solo organizamos eventos, creamos una familia de entusiastas. Amistades que duran toda la vida nacen en nuestros tours.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
